'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import TrailDetailsStickyNavSection from '@alltrails/amplitude/enums/TrailDetailsStickyNavSection';
import logTrailDetailsStickyNavClicked from '@alltrails/amplitude/events/logTrailDetailsStickyNavClicked';
import useIntersectionObserverCallback from '@alltrails/core/hooks/useIntersectionObserverCallback';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import TabBar from '@alltrails/denali/components/TabBar';
import { DirectionsCta, ShareCta } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import SaveCta from '@/components/SaveCta';
import { CONDITIONS_SECTION_ID, HTT_SECTION_ID, NEARBY_TRAILS_SECTION_ID, OVERVIEW_SECTION_ID, REVIEWS_SECTION_ID } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import SectionNavId from '@/utils/SectionNavId';
import { setActiveSection, setIsScrollingViaTabs } from '@/utils/trailPageSlice';
import CustomizeRouteButton from '../Overview/CustomizeRouteButton';
import styles from './DesktopNav.module.scss';

function getAnalyticsNavSection(tabId: SectionNavId): TrailDetailsStickyNavSection {
  switch (tabId) {
    case OVERVIEW_SECTION_ID:
      return 'overview';
    case CONDITIONS_SECTION_ID:
      return 'conditions';
    case REVIEWS_SECTION_ID:
      return 'reviews';
    case HTT_SECTION_ID:
      return 'hit the trail';
    case NEARBY_TRAILS_SECTION_ID:
      return 'nearby';
    default:
      return 'overview';
  }
}

type DesktopNavProps = {
  trailDetails: TrailDetails;
  hasAdminToolbar: boolean;
  seoTestHideAllReviewsForLoggedOutUsers?: boolean;
};

const DesktopNav = ({ trailDetails, hasAdminToolbar, seoTestHideAllReviewsForLoggedOutUsers }: DesktopNavProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const stickyNavRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const ctasRef = useRef<HTMLDivElement>(null);
  const smallNav = useMediaQuery('(max-width: 960px)');
  const { id } = trailDetails;

  const activeSection = useAppSelector(state => state.trailPage.activeSection);

  const stickyObserver = useIntersectionObserverCallback(
    ([e]) => {
      const isSticking = e.intersectionRatio < 1;
      e.target.classList.toggle(styles.sticking, isSticking);
      navRef.current?.classList.toggle(styles.navSticking, isSticking);
      ctasRef.current?.classList.toggle(styles.ctasSticking, isSticking);
    },
    { threshold: 1 }
  );

  const sectionLinks = useMemo(
    () => [
      { id: OVERVIEW_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Overview' }) },
      { id: CONDITIONS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Conditions' }) },
      ...(seoTestHideAllReviewsForLoggedOutUsers ? [] : [{ id: REVIEWS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Reviews' }) }]),
      ...(!smallNav
        ? [
            { id: HTT_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Hit the trail' }) },
            { id: NEARBY_TRAILS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Nearby' }) }
          ]
        : [])
    ],
    [intl, smallNav, seoTestHideAllReviewsForLoggedOutUsers]
  );

  const onTabChange = useCallback(
    (tabId: string) => {
      logTrailDetailsStickyNavClicked({
        current_nav_section: getAnalyticsNavSection(activeSection),
        section_clicked: getAnalyticsNavSection(tabId as SectionNavId),
        trail_id: id
      });
      dispatch(setActiveSection(tabId as SectionNavId));
      dispatch(setIsScrollingViaTabs(true));

      window.addEventListener(
        'scrollend',
        () => {
          dispatch(setIsScrollingViaTabs(false));
        },
        { once: true }
      );
      document.getElementById(tabId)?.scrollIntoView({ behavior: 'smooth' });
    },
    [activeSection, dispatch, id]
  );

  useEffect(() => {
    const stickyNav = stickyNavRef.current;
    if (stickyNav && stickyObserver) {
      stickyObserver.observe(stickyNav);
    }

    return () => {
      stickyObserver?.disconnect();
    };
  }, [stickyObserver]);

  return (
    <div className={classNames(styles.stickyNavContainer, { [styles.hasAdminToolbar]: hasAdminToolbar })} ref={stickyNavRef}>
      <div className={styles.stickyNav} ref={navRef}>
        <TabBar
          activeTab={activeSection}
          className={styles.tabBar}
          hideBottomBorder
          indicatorPosition="top"
          onChange={onTabChange}
          tabs={sectionLinks}
        />
        <div className={styles.ctas} ref={ctasRef}>
          <CustomizeRouteButton trailSlug={trailDetails.slug} trailId={trailDetails.id} analyticsLocation="trail details navbar" isDesktopCta />
          <SaveCta buttonLocation="header bar" trailId={id} />
          <ShareCta trailDetails={trailDetails} buttonLocation="header bar" />
          <DirectionsCta trailId={id} location={trailDetails.location} buttonLocation="header bar" />
        </div>
      </div>
    </div>
  );
};

export default DesktopNav;
